import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Activity, Shield, Loader2, Lock, Mail, KeyRound } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslation } from '../i18n';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needs2FA, setNeeds2FA] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { email, password };
      if (needs2FA) payload.totp_code = totpCode;

      const res = await axios.post(`${API}/admin/login`, payload);

      if (res.data.requires_2fa) {
        setNeeds2FA(true);
        toast.info('Digite o código do Google Authenticator');
        setLoading(false);
        return;
      }

      localStorage.setItem('admin_token', res.data.session_token);
      toast.success('Login realizado!');
      navigate('/admin');
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('Muitas tentativas. Aguarde alguns instantes.');
      } else {
        toast.error(error.response?.data?.detail || 'Credenciais inválidas');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4" data-testid="admin-login-page">
      <Toaster position="top-center" />
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-800 rounded-2xl mb-4">
            <Shield className="w-8 h-8 text-blue-400" />
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Activity className="w-6 h-6 text-blue-400" />
            <span className="text-xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>Vital</span>
          </div>
          <p className="text-slate-400 text-sm">{t('admin.welcome', 'Painel Administrativo')}</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
          {!needs2FA ? (
            <>
              <div className="space-y-2">
                <Label className="text-slate-300">{t('login.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@vital.com"
                    required
                    data-testid="admin-email-input"
                    className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Sua senha"
                    required
                    data-testid="admin-password-input"
                    className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2 mb-2">
                <KeyRound className="w-5 h-5 text-blue-400" />
                <span className="text-sm font-semibold text-slate-300">Verificação em Duas Etapas</span>
              </div>
              <p className="text-xs text-slate-400 text-center">
                Abra o Google Authenticator e digite o código de 6 dígitos
              </p>
              <div className="space-y-2">
                <Label className="text-slate-300">Código 2FA</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  autoFocus
                  data-testid="admin-totp-input"
                  className="bg-slate-800 border-slate-700 text-white text-center text-2xl tracking-widest font-mono placeholder:text-slate-600"
                />
              </div>
              <button
                type="button"
                onClick={() => { setNeeds2FA(false); setTotpCode(''); }}
                className="text-xs text-slate-500 hover:text-slate-300 w-full text-center"
              >
                Voltar ao login
              </button>
            </div>
          )}
          <Button
            type="submit"
            disabled={loading || (needs2FA && totpCode.length < 6)}
            data-testid="admin-login-btn"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
            {needs2FA ? t('common.confirm') : t('login.submit')}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
